// ---------------------------------------------------------------------------
// The filter state shared by every chart and by the data table.
//
// This site has no database and no login, so all state lives in the browser.
// It lives in the URL specifically: a public explainer has to let a reader send
// someone a link to the filtered view they are actually looking at.
//
// Vue's reactivity works outside components, so this is a plain module — any
// component can import `filters` and derive from it with computed().
// ---------------------------------------------------------------------------

import { reactive, watch } from 'vue'

// The dashboard does not open on everything it has: court cases are out,
// because the Office answers for its own decisions, not for how long a court
// then took over them. It is an ordinary filter value, not a hidden baseline —
// the pill shows it set and the address bar carries it.
//
// Orders are out too, but that one is not offered: see visibleRows.
export const DEFAULTS = { soud: 'ne' }

// A flat record of dimension -> value, seeded from the query string on load.
// Values are strings, because that is what a query string can carry back.
export const filters = reactive({
    ...DEFAULTS,
    ...Object.fromEntries(new URLSearchParams(window.location.search)),
})

/**
 * The filter as a query string, holding only what differs from the defaults.
 *
 * Comparing against the defaults rather than against empty is what makes
 * "Soud: nerozlišovat" shareable: it is an empty value, so a link that simply
 * left empties out would come back with the default reapplied.
 */
export function queryString() {
    return new URLSearchParams(
        Object.entries(filters)
            .filter(([, value]) => value != null)
            .filter(([key, value]) => value !== (DEFAULTS[key] ?? ''))
    ).toString()
}

// Mirror the state into the address bar. replaceState rather than pushState:
// narrowing a filter is not navigation, and it must not stuff the back button
// with one entry per keystroke.
watch(filters, () => {
    const query = queryString()

    window.history.replaceState(null, '', query ? `?${query}` : window.location.pathname)
})

// The strip that holds the slicers is quiet until one of them is set, and then
// it takes on a surface. CSS cannot look up the tree, so the fact lands on
// <html> and the band styles itself from there. `q` is the table's own search
// box and does not count.
function markNarrowed() {
    const on = Boolean(filters.rok || filters.obor || filters.soud)

    if (on) document.documentElement.dataset.filtered = ''
    else delete document.documentElement.dataset.filtered
}

watch(filters, markNarrowed, { immediate: true })

// Carry the filter across the dashboard. A reader who narrows to one sector and
// one year is reading a story about that sector, and it should still be the same
// story on the next page — so the links themselves get the query string rather
// than a click handler. That way middle-click, "open in new tab" and copying the
// address all keep it too.
export function keepFilterOnLinks() {
    const links = [...document.querySelectorAll('a[href]:not([data-nofilter])')].filter(
        (link) => link.origin === window.location.origin && link.pathname.startsWith('/uohs/')
    )

    if (!links.length) return

    watch(
        filters,
        () => {
            const query = queryString()
            links.forEach((link) => { link.search = query })
        },
        { immediate: true }
    )
}

/** Back to the state the dashboard opens in, not to an empty filter. */
export function reset() {
    Object.keys(filters).forEach((key) => delete filters[key])
    Object.assign(filters, DEFAULTS)
}
